"use client";

import { FilterBar } from "./filter-bar";
import { QuickFilterMenu } from "./quick-filter-menu";


import { ActiveFilters } from "./active-filters";
import { ColumnPicker } from "./column-picker";
import { SmartTableGrid } from "./smart-table-grid";
import { SmartTablePagination } from "./smart-table-pagination";
import { SmartTableToolbar } from "./smart-table-toolbar";
import { SmartTableModelContext, toModelContextValue } from "./smart-table-model-context";
import { useSmartTableModel } from "./use-smart-table-model";
import type { SmartTableProps } from "./smart-table-props";

export { formatCell } from "./cell-renderers";
export { SmartEditModal, type SmartEditModalProps } from "./smart-edit-modal";

export function SmartTable<TData>(props: SmartTableProps<TData>) {
  const m = useSmartTableModel<TData>(props);
  const { columns, data, isLoading, error, filters, lists, quickFilter, setQuickFilter, t, defaultFilter, Button, EmptyState, ErrorState, onFiltersChangeWithHistory } = m;
  return (
    <SmartTableModelContext.Provider value={toModelContextValue(m)}>
      <div className="space-y-3">
        <FilterBar
          columns={columns}
          lists={lists}
          filters={filters}
          onFiltersChange={onFiltersChangeWithHistory}
          defaultFilter={defaultFilter}
        />
        <SmartTableToolbar />
        <ColumnPicker />
        <ActiveFilters />
        {error !== null && <ErrorState message={error} />}
        {!isLoading && error === null && data.length === 0 && (
          <EmptyState
            message={
              filters.length > 0 ? t("table.empty.filtered") : t("table.empty")
            }
          >
            {filters.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onFiltersChangeWithHistory([])}
                className="mt-2"
              >
                {t("table.filters.clearAll")}
              </Button>
            )}
          </EmptyState>
        )}
        <SmartTableGrid />
        <SmartTablePagination />
        {quickFilter && (
          <QuickFilterMenu
            column={quickFilter.column}
            value={quickFilter.value}
            x={quickFilter.x}
            y={quickFilter.y}
            onApply={(clause) => {
              const existing = filters.find((f) => f.column === clause.column);
              if (existing) {
                onFiltersChangeWithHistory(
                  filters.map((f) => (f.column === clause.column ? clause : f)),
                );
              } else {
                onFiltersChangeWithHistory([...filters, clause]);
              }
            }}
            onClose={() => setQuickFilter(null)}
          />
        )}
      </div>
    </SmartTableModelContext.Provider>
  );
}
